import { canAccessWorkflowCapability } from '@/lib/capabilities';
import { canManagePayRuns } from './pay-run-access';
import { timesheetApprovalHandoff } from './payroll-blockers';
import { WORK_STATE_LABELS, type WorkRunState } from './rebuild/work-view';

export function isPayrollStatusReviewer(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'workforce.payroll.review') && !canManagePayRuns(role);
}

export interface CoordinatorPayrollStatus {
  statusLabel: string;
  holds: { id: string; date: string; workerName?: string; href: string; reason: string }[];
}

/** Project only timesheets admitted by the caller's existing timesheet reader. */
export function coordinatorTimesheetHolds(
  visible: readonly {
    id: string;
    date: string;
    roster_assignment_id?: string | null;
    workerName?: string;
  }[],
  readiness: readonly {
    severity: string;
    resourceType?: string;
    resourceId?: string;
    code?: string;
  }[],
  pricing: readonly { source_type?: string; source_id?: string; code?: string }[],
): CoordinatorPayrollStatus['holds'] {
  const held = new Set(
    readiness
      .filter((row) => row.severity === 'HARD' && row.resourceType === 'timesheet')
      .map((row) => row.resourceId),
  );
  const heldAssignments = new Set(
    readiness
      .filter((row) => row.severity === 'HARD' && row.resourceType === 'roster_assignment')
      .map((row) => row.resourceId),
  );
  for (const row of visible)
    if (row.roster_assignment_id && heldAssignments.has(row.roster_assignment_id)) held.add(row.id);
  for (const row of pricing) if (row.source_type === 'TIMESHEET_SEGMENT') held.add(row.source_id);
  return visible
    .filter((row) => held.has(row.id))
    .map((row) => ({
      id: row.id,
      date: row.date,
      workerName: row.workerName,
      href: `/roster/timesheets?${new URLSearchParams({
        timesheet_id: row.id,
        resolve: '1',
        ...(pricing.some(
          (hold) =>
            hold.source_id === row.id && hold.code === 'payroll_timesheet_hold_approval_superseded',
        )
          ? { hold: 'approval_superseded' }
          : {}),
      })}`,
      reason:
        [
          ...readiness.filter((hold) => hold.resourceId === row.id),
          ...pricing.filter((hold) => hold.source_id === row.id),
        ]
          .map((hold) => timesheetApprovalHandoff(hold.code)?.owner)
          .find(Boolean) ??
        'This timesheet is held for payroll. Open it to review and fix the time or pay details.',
    }));
}

export function coordinatorRunStatus(state: string | null, runStatus: string | null): string {
  if (state && Object.hasOwn(WORK_STATE_LABELS, state))
    return WORK_STATE_LABELS[state as WorkRunState];
  if (runStatus === null) return 'No pay run created for this period';
  const labels: Record<string, string> = {
    DRAFT: 'Draft pay run',
    IN_REVIEW: 'Pay run in review',
    APPROVED: 'Pay run approved',
    EXPORTED: 'Pay run sent to the payroll provider',
    PUSHED_TO_XERO: 'Pay run sent to Xero',
    RECONCILED: 'Pay run checked against the payroll provider',
    PAID: 'Payment recorded',
    CANCELLED: 'Pay run cancelled',
    SUPERSEDED: 'Pay run replaced',
  };
  return labels[runStatus] ?? 'Pay run status unavailable';
}
